import type { GamakaProfileEntry, PitchBand } from '@swarasense/shared';
import { centsToY, gamakaZones, targetBands, timeToX } from './ribbon-geometry.js';
import type { RibbonView } from './ribbon-geometry.js';
import { cssVar, THEME_CHANGE_EVENT } from './theme/theme.js';

/**
 * The signature Live view (Task 4, replacing the vertical ladder): a
 * horizontally scrolling pitch ribbon. Time runs left→right with "now" at the
 * right edge; pitch is the vertical axis. Target swara bands are horizontal
 * reference lines, gamaka-eligible swaras get a shaded/dashed zone around
 * their band, and the sung pitch is drawn as one continuous curve — so a
 * kampita/jaru reads as the wobble/glide it is, not a step function.
 */

/** One frame to plot. `cents` is the folded pitch (0–1200, same convention as
 * the target scale from `resolveScoring`); `band` grades it (already
 * hysteresis-adjusted by the caller — see `app.ts`'s `onPitch`, the pre-redesign
 * `screens/live.ts` this originally referenced having since been deleted); `transition`
 * marks a fast glide (dimmed, not graded as a wrong note); `confident` is
 * false for a marginal/near-threshold detection (drawn grey — not scored). */
export interface RibbonFrame {
  tMs: number;
  cents: number;
  band: PitchBand;
  transition: boolean;
  confident: boolean;
}

interface StoredFrame {
  tMs: number;
  cents: number | null; // null = a gap (silence) that breaks the contour
  band: PitchBand;
  transition: boolean;
  confident: boolean;
}

const WINDOW_MS = 6000;
const PAD_Y = 24;
/** Throttle for the canvas's `aria-label` — screen readers don't need every ~25ms frame. */
const ARIA_THROTTLE_MS = 250;

/** Ribbon colours, all read from the current theme's CSS tokens (tokens.css) — never a
 * literal hex, so a theme switch retints the canvas without redrawing logic. */
interface Palette {
  excellent: string;
  good: string;
  off: string;
  /** Committed out-of-raga ("wrong") frames — Task 4 softened this from the
   * kumkum-red `--band-wrong` alarm to the desaturated `--away` token (a
   * "drifting away" look, not a scold). Kept the `wrong` field name since it
   * still indexes by `PitchBand`'s `'wrong'` value below. */
  wrong: string;
  gamaka: string;
  /** Low-confidence / not-scored frames — drawn DASHED (see the contour draw
   * loop below), not just a dimmer shade, so it reads as qualitatively
   * distinct from the also-`--muted-faint`-derived "away" segment: dashed
   * grey = "couldn't hear you", thin solid `--away` = "drifted, but I heard
   * you". */
  grey: string;
  saLine: string;
  saText: string;
  labelText: string;
  serif: string;
}

function readPalette(): Palette {
  return {
    excellent: cssVar('--band-excellent'),
    good: cssVar('--band-good'),
    off: cssVar('--band-off'),
    wrong: cssVar('--away'),
    gamaka: cssVar('--gamaka'),
    grey: cssVar('--muted-faint'),
    saLine: cssVar('--sa-line'),
    // Fix (contrast blocker): the "Ṡ" label is drawn on the ribbon's canvas
    // background, not on a solid accent fill — `--accent-ink` is reserved for
    // on-accent-fill text/icons (see styles.css's `.btn.primary`). `--accent`
    // (madder) is what actually reads against the ground.
    saText: cssVar('--accent'),
    labelText: cssVar('--ink-strong'),
    serif: cssVar('--font-serif'),
  };
}

const fold = (c: number): number => ((c % 1200) + 1200) % 1200;

export class Ribbon {
  private readonly canvas: HTMLCanvasElement;
  /** A visually-hidden `aria-live="polite"` text node, throttled in lockstep
   * with the canvas's own `aria-label` (`updateAria`) — a live region's
   * announced-on-change behaviour is spec'd around its TEXT CONTENT, not
   * attribute mutations, so relying on the canvas's `aria-label` alone (which
   * isn't itself a live region) would only get read out if focus happened to
   * land there. This is the actual thing screen readers announce. */
  private readonly liveRegion: HTMLElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly resizeObserver: ResizeObserver;
  private readonly reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  private targetCents: Record<string, number> = {};
  private gamakaProfile: Record<string, GamakaProfileEntry> = {};
  private frames: StoredFrame[] = [];
  private dpr = 1;
  private raf = 0;
  private palette: Palette;
  private lastAriaAt = 0;
  private readonly onThemeChange = (): void => {
    this.palette = readPalette();
    this.draw();
  };
  private readonly onReducedMotionChange = (): void => {
    if (this.reducedMotionQuery.matches) cancelAnimationFrame(this.raf);
    else this.loop();
  };

  constructor(container: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.setAttribute('role', 'img');
    this.canvas.setAttribute('aria-label', 'Pitch ribbon: no input');
    container.appendChild(this.canvas);
    this.liveRegion = document.createElement('span');
    this.liveRegion.className = 'sr-only';
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    container.appendChild(this.liveRegion);
    this.ctx = this.canvas.getContext('2d')!;
    this.palette = readPalette();
    document.addEventListener(THEME_CHANGE_EVENT, this.onThemeChange);
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    if (!this.reducedMotionQuery.matches) this.loop();
  }

  /** Sets the raga's target cents (one band per swara) and gamaka profile
   * (which of them get a shaded oscillation zone). Redraws immediately so the
   * scale is visible before the first pitch sample arrives. */
  setScale(
    targetCents: Record<string, number>,
    gamakaProfile: Record<string, GamakaProfileEntry>,
  ): void {
    this.targetCents = targetCents;
    this.gamakaProfile = gamakaProfile;
    this.draw();
  }

  /** Appends one plotted frame, or `null` to mark a gap (no input) that breaks
   * the contour — mirrors the ladder's `push(null, tone)` silence handling. */
  push(sample: RibbonFrame | null): void {
    const frame: StoredFrame =
      sample === null
        ? { tMs: performance.now(), cents: null, band: 'off', transition: false, confident: false }
        : sample;
    this.frames.push(frame);
    this.updateAria(frame);
    if (this.reducedMotionQuery.matches) this.draw();
  }

  resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.canvas.clientWidth || this.canvas.parentElement!.clientWidth;
    const h = this.canvas.clientHeight || this.canvas.parentElement!.clientHeight;
    this.canvas.width = Math.max(1, Math.round(w * this.dpr));
    this.canvas.height = Math.max(1, Math.round(h * this.dpr));
    this.draw();
  }

  /** Empties the plotted trace (e.g. starting a fresh take) without touching the scale. */
  clear(): void {
    this.frames = [];
    this.draw();
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
    document.removeEventListener(THEME_CHANGE_EVENT, this.onThemeChange);
    this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
    this.resizeObserver.disconnect();
  }

  /** Nearest target swara to a folded cents value — for the throttled ARIA label only. */
  private nearestSymbol(cents: number): string | null {
    const entries = Object.entries(this.targetCents);
    if (entries.length === 0) return null;
    const pc = fold(cents);
    let best: string | null = null;
    let bestAbs = Infinity;
    for (const [sym, tc] of entries) {
      let d = pc - fold(tc);
      if (d > 600) d -= 1200;
      else if (d < -600) d += 1200;
      if (Math.abs(d) < bestAbs) {
        bestAbs = Math.abs(d);
        best = sym;
      }
    }
    return best;
  }

  private updateAria(frame: StoredFrame): void {
    const now = performance.now();
    if (now - this.lastAriaAt < ARIA_THROTTLE_MS) return;
    this.lastAriaAt = now;
    let label: string;
    if (frame.cents === null) {
      label = 'Pitch ribbon: no input';
    } else {
      const sym = this.nearestSymbol(frame.cents) ?? '—';
      // Encouraging-feedback Task 4: no scold words in the announced label
      // either — a screen-reader user deserves the same softened tone a
      // sighted child gets from the ribbon's visual "away" treatment.
      const verdict = !frame.confident
        ? 'listening'
        : frame.transition
          ? 'gliding'
          : frame.band === 'excellent' || frame.band === 'good'
            ? 'in tune'
            : frame.band === 'wrong'
              ? 'drifting'
              : 'nearly there';
      label = `Pitch ribbon: ${sym} — ${verdict}`;
    }
    this.canvas.setAttribute('aria-label', label);
    // The actual announced-on-change text (see `liveRegion`'s doc above) —
    // the canvas's own `aria-label` only describes it to focus/inspection.
    this.liveRegion.textContent = label;
  }

  private draw(): void {
    const { ctx, palette: pal } = this;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const W = this.canvas.width / this.dpr;
    const H = this.canvas.height / this.dpr;
    ctx.clearRect(0, 0, W, H);
    if (Object.keys(this.targetCents).length === 0) return;

    const labelW = Math.min(40, W * 0.09);
    const trackLeft = labelW + 8;
    const trackRight = W - 10;
    const view: RibbonView = { minCents: 0, maxCents: 1200, height: H, pad: PAD_Y };

    // Gamaka zones first (under the target lines and contour): a shaded,
    // dashed-edge band per oscillating swara, sized from its profile.
    for (const zone of gamakaZones(this.gamakaProfile, this.targetCents, view)) {
      ctx.save();
      ctx.globalAlpha = 0.14;
      ctx.fillStyle = pal.gamaka;
      ctx.fillRect(trackLeft, zone.yTop, trackRight - trackLeft, zone.yBottom - zone.yTop);
      ctx.restore();
      ctx.save();
      ctx.strokeStyle = pal.gamaka;
      ctx.globalAlpha = 0.65;
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(trackLeft, zone.yTop);
      ctx.lineTo(trackRight, zone.yTop);
      ctx.moveTo(trackLeft, zone.yBottom);
      ctx.lineTo(trackRight, zone.yBottom);
      ctx.stroke();
      ctx.restore();
    }

    // Target bands + swara labels (serif — the ribbon's own type voice). The
    // raga's own swaras (targetCents) are all folded into 0–1200 (madhya
    // octave) and never include the octave Sa, so it's appended here at
    // `maxCents` — every raga, pentatonic or not, reads as a full octave
    // S(bottom) → Ṡ(top), matching the static review plot's own
    // `rung('Ṡ', 1200, …)` (see results.ts). Visual only: it isn't added to
    // `targetCents`, so scoring/aria nearest-symbol matching is unaffected.
    ctx.textBaseline = 'middle';
    const bands = [...targetBands(this.targetCents, view), { sym: 'Ṡ', y: centsToY(view.maxCents, view) }];
    for (const band of bands) {
      const isSa = band.sym === 'S' || band.sym === 'Ṡ';
      ctx.save();
      ctx.strokeStyle = isSa ? pal.saLine : pal.labelText;
      ctx.globalAlpha = isSa ? 0.9 : 0.32;
      ctx.lineWidth = isSa ? 1.5 : 1;
      ctx.beginPath();
      ctx.moveTo(trackLeft, band.y);
      ctx.lineTo(trackRight, band.y);
      ctx.stroke();
      ctx.restore();
      ctx.fillStyle = isSa ? pal.saText : pal.labelText;
      ctx.font = `${isSa ? 700 : 600} ${isSa ? 17 : 14}px ${pal.serif}`;
      ctx.textAlign = 'right';
      ctx.fillText(band.sym, trackLeft - 6, band.y);
    }

    // The sung contour: one continuous polyline, coloured per-frame by band
    // token, dimmed mid-transition. Two DIFFERENT muted states, made
    // qualitatively distinct (not just a width/alpha scale of each other —
    // review feedback caught that a thin/dim "away" segment and a
    // low-confidence "grey" segment were too easy to confuse at a glance,
    // both being the same `--muted-faint`-derived hue):
    //   - low-confidence (unscoreable — "couldn't hear you") → DASHED grey.
    //   - away (committed out-of-raga — "drifted, but I heard you") → thin
    //     SOLID `--away`.
    // Gaps (silence) break the line rather than smearing across them.
    const now = performance.now();
    this.frames = this.frames.filter((f) => now - f.tMs <= WINDOW_MS);
    const timeView = { windowMs: WINDOW_MS, width: trackRight - trackLeft };
    const xOf = (tMs: number) => trackLeft + timeToX(tMs, now, timeView);
    const colorFor = (f: StoredFrame) => (f.confident ? pal[f.band] : pal.grey);
    // A committed out-of-raga frame ("away") — Task 4's gentle "drifting away"
    // look. It must read as distinct from in-tune WITHOUT relying on the
    // (already-desaturated) colour alone, so it's drawn thinner too.
    const isAway = (f: StoredFrame) => f.confident && f.band === 'wrong';

    const NORMAL_LINE_WIDTH = 2.5;
    const AWAY_LINE_WIDTH = 1.25;
    const AWAY_ALPHA = 0.55;
    const LOW_CONFIDENCE_DASH: [number, number] = [4, 4];
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    let prev: { x: number; y: number } | null = null;
    let latest: { x: number; y: number; color: string; transition: boolean; away: boolean } | null =
      null;
    for (const f of this.frames) {
      if (f.cents === null) {
        prev = null;
        latest = null;
        continue;
      }
      const point = { x: xOf(f.tMs), y: centsToY(f.cents, view) };
      const color = colorFor(f);
      const away = isAway(f);
      if (prev) {
        ctx.globalAlpha = f.transition ? 0.4 : away ? AWAY_ALPHA : 1;
        ctx.lineWidth = away ? AWAY_LINE_WIDTH : NORMAL_LINE_WIDTH;
        // Set (or clear) the dash pattern on every segment, unconditionally
        // — never inherited from the previous iteration, so a low-confidence
        // stretch can never bleed a dash into the solid segment after it.
        ctx.setLineDash(f.confident ? [] : LOW_CONFIDENCE_DASH);
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.lineWidth = NORMAL_LINE_WIDTH;
      }
      prev = point;
      latest = { x: point.x, y: point.y, color, transition: f.transition, away };
    }
    // Belt-and-suspenders: the loop above always sets an explicit dash state
    // per segment, but reset it once more here so nothing drawn AFTER the
    // contour (the marker below, or a future addition) could ever inherit a
    // dash left over from the last segment.
    ctx.setLineDash([]);

    // Current-pitch marker at the leading edge, echoing the ladder's glow dot.
    // An "away" marker is smaller and un-glowing — quiet, not alarming.
    if (latest) {
      ctx.save();
      ctx.globalAlpha = latest.transition ? 0.55 : latest.away ? AWAY_ALPHA : 1;
      ctx.fillStyle = latest.color;
      if (!latest.away) {
        ctx.shadowColor = latest.color;
        ctx.shadowBlur = 10;
      }
      ctx.beginPath();
      ctx.arc(trackRight, latest.y, latest.away ? 3.5 : 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  private loop = (): void => {
    this.draw();
    this.raf = requestAnimationFrame(this.loop);
  };
}
